import { Navigate, Outlet, matchPath, useLocation } from 'react-router-dom'

import { useLoginData } from '~/features/authentication/data.ts'
import { createFlashMessage } from '~/features/flash-messages/utils.ts'
import { useWorkspace } from '~/features/workspaces/data.ts'

import { routes } from '../constants.ts'
import { Spinner } from './Loading/index.tsx'

/*
  Redirects users who try to visit a workspace they're not a member of or that doesn't exist.
*/
export function RedirectUnknownWorkspace() {
  const location = useLocation()
  const { data } = useLoginData()

  // If we don't have login data, wait it out
  if (!data) return null

  // If we have login data, and the user is not logged in, redirect them to the login page.
  if (data && !data.login_data.loggedIn) {
    createFlashMessage({ text: 'Please log in to access this page', isError: true })
    return <Navigate to={`/c/login/?loggedOut=1&redirect=${location.pathname}`} />
  }

  // Kick in workspace redirect logic
  return <RedirectWorkspace />
}

// This logic is in its own component so that we can use a hook that requires being logged in below an if/return statement.
function RedirectWorkspace() {
  const location = useLocation()
  const { data, isLoading, error } = useWorkspace()

  // Are we matching with a valid route that doesn't include the app or admin dashboard?
  // If we match any other valid route, don't redirect the user.
  if (
    routes
      .filter((x) => !x.includes(':workspace'))
      .filter((x) => !x.startsWith(`/c/admin`))
      .some((x) => matchPath(x, location.pathname))
  ) {
    return <Outlet />
  }

  if (isLoading) {
    return <Spinner />
  }

  if (error) {
    throw error
  }

  if (!data) {
    // This shouldn’t occur!
    return <Spinner />
  }

  // If the user visits an admin route while they're not an admin, redirect them to the dashboard of their first workspace.
  if (
    !(data?.isAdmin || data?.isFounder) &&
    routes.filter((x) => x.startsWith(`/c/admin`)).some((x) => matchPath(x, location.pathname))
  ) {
    const [{ workspace }] = data.memberships
    return <Navigate to={`/c/${workspace.slug}`} />
  }

  // If the user visits an admin route while they're an admin, don't redirect them or render a 404
  if (
    (data.isAdmin || data.isFounder) &&
    routes.filter((x) => x.startsWith(`/c/admin`)).some((x) => matchPath(x, location.pathname))
  ) {
    return <Outlet />
  }

  const workspaceRoute = routes
    .filter((x) => x.includes(':workspace'))
    .find((x) => matchPath(x, location.pathname))

  // If we are matching a workspace route
  if (workspaceRoute) {
    const workspaceRouteMatch = matchPath(workspaceRoute, location.pathname)

    // Just a sanity check for TS, this should never happen
    if (!workspaceRouteMatch) {
      return null
    }

    const { workspace: workspaceSlug } = workspaceRouteMatch.params

    // If the user visits one of their own workspaces, don't to redirect them.
    if (data.memberships.some((x) => x.workspace.slug === workspaceSlug)) {
      return <Outlet />
    }

    // If an admin visits a workspace they're not a member of, don't redirect them but let admin auto impersonation kick in.
    if (data?.isAdmin && !data.memberships.some((x) => x.workspace.slug === workspaceSlug)) {
      return <Outlet />
    }

    // If the user visits a workspace they're not a member of, redirect them to the dashboard of their first workspace.
    if (data.memberships.every((x) => x.workspace.slug !== workspaceSlug)) {
      const [{ workspace }] = data.memberships
      return <Navigate to={`/c/${workspace.slug}`} />
    }
  }

  return null
}
